import React from 'react';

import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ValidationForm, TextInput } from 'react-bootstrap4-form-validation';
import validator from 'validator';
import InfoTooltipComponent from '../../commons/infoTooltip/infoTooltipComponent';
import '../../assets/validatedCheckbox.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import fi from 'date-fns/locale/fi';
import { getPrivilegeString } from '../../../utils/privileges';

registerLocale('fi', fi);

const AddMemberView = ({
    firstName,
    lastName,
    utuAccount,
    email,
    homeTown,
    tyyMember,
    privileges,
    membershipSince,
    membershipUntil,
    handleAddMember,
    handleInputChange,
    handleMembershipStartsChange,
    handleMembershipEndsChange,
    handlePrivilegeStartChange,
    handlePrivilegeEndChange,
    handlePrivilegeChange,
    handleRemovePrivilege,
    handleAddPrivilege,
    success,
    message,
}) => {
    const { t, i18n } = useTranslation();
    // 'en' is react-datepicker's built-in default; everything else falls
    // back to Finnish alongside the app locales.
    const datePickerLocale = i18n.language?.startsWith('en') ? 'en' : 'fi';

    return (
        <div className="container">
            <ValidationForm
                onSubmit={(e) => {
                    e.preventDefault();
                    handleAddMember(e);
                }}
            >
                <Form.Group>
                    <Form.Label>{t('memberUpdate.firstName')}</Form.Label>
                    <TextInput
                        name="firstName"
                        id="firstName"
                        defaultValue={firstName}
                        errorMessage={{
                            required: t('memberUpdate.firstNameRequired'),
                            pattern: t('memberUpdate.firstNameInvalid'),
                        }}
                        required
                        pattern="[a-zA-Z\u00c0-\u017e- ]{2,40}$"
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('memberUpdate.lastName')}</Form.Label>
                    <TextInput
                        name="lastName"
                        id="lastName"
                        defaultValue={lastName}
                        errorMessage={{
                            required: t('memberUpdate.lastNameRequired'),
                            pattern: t('memberUpdate.lastNameInvalid'),
                        }}
                        required
                        pattern="[a-zA-Z\u00c0-\u017e- ]{2,25}$"
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('memberUpdate.utuAccountLabel')}</Form.Label>
                    <TextInput
                        name="utuAccount"
                        id="utuAccount"
                        defaultValue={utuAccount}
                        errorMessage={{
                            required: t('memberUpdate.utuAccountRequired'),
                            pattern: t('memberUpdate.utuAccountInvalid'),
                        }}
                        pattern="[a-öA-Ö.]{2,30}$"
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('memberUpdate.email')}</Form.Label>
                    <TextInput
                        name="email"
                        defaultValue={email}
                        id="email"
                        errorMessage={{
                            required: t('memberUpdate.emailRequired'),
                            validator: t('memberUpdate.emailInvalid'),
                        }}
                        validator={validator.isEmail}
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>{t('memberUpdate.homeTown')}</Form.Label>
                    <TextInput
                        name="homeTown"
                        id="homeTown"
                        defaultValue={homeTown}
                        errorMessage={{
                            required: t('memberUpdate.homeTownRequired'),
                            pattern: t('memberUpdate.homeTownInvalid'),
                        }}
                        required
                        pattern="[a-zA-Z\u00c0-\u017e- ]{2,25}$"
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        onChange={handleInputChange}
                        name="tyyMember"
                        label={
                            <>
                                {t('memberUpdate.tyyMember')}{' '}
                                <InfoTooltipComponent
                                    text={t('common.tyyMemberInfo')}
                                />
                            </>
                        }
                    />
                </Form.Group>
                <hr />

                {privileges.map((privilege, index) => (
                    <div
                        key={index}
                        style={{
                            backgroundColor: '#ececec',
                            padding: '15px',
                            margin: '10px',
                            borderRadius: '5px',
                        }}
                    >
                        <Form.Group>
                            <Form.Label>
                                {t('adminMemberUpdate.role')}
                            </Form.Label>
                            <Form.Control
                                as="select"
                                name={`privileges[${index}].role`}
                                value={privilege.role}
                                onChange={(e) =>
                                    handlePrivilegeChange(index, e.target.value)
                                }
                            >
                                <option value="kiltisban">
                                    {getPrivilegeString(['kiltisban'])}
                                </option>
                                <option value="admin">
                                    {getPrivilegeString(['admin'])}
                                </option>
                                <option value="chairman">
                                    {getPrivilegeString(['chairman'])}
                                </option>
                                <option value="vicechairman">
                                    {getPrivilegeString(['vicechairman'])}
                                </option>
                                <option value="boardmember">
                                    {getPrivilegeString(['boardmember'])}
                                </option>
                                <option value="functionary">
                                    {getPrivilegeString(['functionary'])}
                                </option>
                                <option value="tutor">
                                    {getPrivilegeString(['tutor'])}
                                </option>
                                <option value="bachelorfresher">
                                    {getPrivilegeString(['bachelorfresher'])}
                                </option>
                                <option value="masterfresher">
                                    {getPrivilegeString(['masterfresher'])}
                                </option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>
                                {t('adminMemberUpdate.starts')}
                            </Form.Label>
                            <DatePicker
                                selected={
                                    privilege.startDate
                                        ? new Date(privilege.startDate)
                                        : null
                                }
                                onChange={(value) =>
                                    handlePrivilegeStartChange(value, index)
                                }
                                dateFormat="dd.MM.yyyy"
                                className="form-control"
                                locale={datePickerLocale}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>
                                {t('adminMemberUpdate.ends')}
                            </Form.Label>
                            <DatePicker
                                selected={
                                    privilege.endDate
                                        ? new Date(privilege.endDate)
                                        : null
                                }
                                onChange={(value) =>
                                    handlePrivilegeEndChange(value, index)
                                }
                                dateFormat="dd.MM.yyyy"
                                className="form-control"
                                locale={datePickerLocale}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Row>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleRemovePrivilege(index)}
                                >
                                    {t('adminMemberUpdate.removePrivilege')}
                                </button>
                            </Form.Row>
                        </Form.Group>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddPrivilege}
                >
                    {t('adminMemberUpdate.addRole')}
                </button>
                <hr />

                <Form.Group>
                    <Form.Check
                        type="checkbox"
                        onChange={handleInputChange}
                        name="accepted"
                        label={t('memberDetails.membershipAccepted')}
                    />
                </Form.Group>
                <div className="row">
                    <div className="col">
                        <Form.Group>
                            <Form.Label className="d-block">
                                {t('adminMemberUpdate.membershipStarts')}
                            </Form.Label>
                            <DatePicker
                                selected={
                                    membershipSince
                                        ? new Date(membershipSince)
                                        : null
                                }
                                onChange={handleMembershipStartsChange}
                                dateFormat="dd.MM.yyyy"
                                className="form-control"
                                locale={datePickerLocale}
                            />
                        </Form.Group>
                    </div>
                    <div className="col">
                        <Form.Group>
                            <Form.Label className="d-block">
                                {t('adminMemberUpdate.membershipEnds')}
                            </Form.Label>
                            <DatePicker
                                selected={
                                    membershipUntil
                                        ? new Date(membershipUntil)
                                        : null
                                }
                                onChange={handleMembershipEndsChange}
                                dateFormat="dd.MM.yyyy"
                                className="form-control"
                                locale={datePickerLocale}
                            />
                        </Form.Group>
                    </div>
                </div>
                <p>{t('adminAddMember.passwordNote')}</p>
                {message && !success ? (
                    <Alert variant={!success ? 'danger' : 'success'}>
                        {t(message.key, message.params)}
                    </Alert>
                ) : null}
                <div>
                    <button className="btn btn-success">
                        {t('adminAddMember.submit')}
                    </button>
                    <Link className="btn btn-secondary secondary" to="/admin">
                        {t('common.back')}
                    </Link>
                </div>
            </ValidationForm>
        </div>
    );
};

export default AddMemberView;
